import { useCallback, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { yakurekiPollKey } from '@/apps/latest/shared/recording-status/usePollEvent'
import { useToast } from '@/shared/contexts/ToastContext'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'
import {
  itemsOfField,
  soapsWithField,
} from '@/apps/latest/components/organisms/PostRecordingView/homeVisitFields'
import { useLineEditing } from '@/apps/latest/components/organisms/PostRecordingView/hooks/useLineEditing'

import { patchHomeVisitReport } from './homeVisitReportApi'

// 訪問報告書の 1 行編集 / 行追加 / チェック (採否) を SOAP と同じ操作感で扱う hook。
// 行編集の状態機械は共通の useLineEditing に集約し、ここでは「home-visit の items 解決」と
// 「home-visit-report PATCH 保存」を注入する。それに加えて採否 (checkbox =
// SoapContentItem.is_selected) の行単位 toggle を持つ。
//
// ── 送るのは触ったキーだけ (log/186) ──────────────────────────────────────
// この API は **送られたキーだけ** を差し替える (back home_care/views.py)。全 blob を送る
// 契約だった頃は、往復中に別の欄を保存した 2 本目が 1 本目の変更を含まない body を送り、
// 先に保存した欄が黙って消えていた。触ったキーだけを送れば、client の cache が古くても
// 触っていない欄は壊れない (= lost update が構造的に起きない)。
//
// 残す 2 点:
//   - onMutate で楽観更新 + onError でロールバック
//     (往復ぶん無反応だとチェックの再クリックを誘発する)
//   - items 解決は cache 起点にする
//     (連打の 2 回目が render 時点の値を見ると、1 回目のトグルが無かったことになる)
// scope の直列化も残す。同じキーへの連続書き込みは順序が入れ替わると結果が変わるため。
export const useHomeVisitReportEditing = ({
  event,
}: {
  event: YakurekiDetail
}) => {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const detailKey = useMemo(() => yakurekiPollKey(event.id), [event.id])

  const { mutateAsync } = useMutation({
    // 同一 event の報告書更新は直列実行する (TanStack Query v5 の mutation scope)。
    scope: { id: `home-visit-report:${event.id}` },

    mutationFn: async ({
      key,
      nextItems,
    }: {
      key: string
      nextItems: SoapContentItem[]
    }) => {
      await patchHomeVisitReport(event.id, { [key]: nextItems })
    },

    onMutate: async ({
      key,
      nextItems,
    }: {
      key: string
      nextItems: SoapContentItem[]
    }) => {
      // 進行中の fetch (在宅は完了後も猶予 polling が回りうる) をキャンセルしてから
      // 楽観更新する。しないと編集前の応答が着地して楽観状態を巻き戻す。
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<YakurekiDetail>(detailKey)
      queryClient.setQueryData<YakurekiDetail>(detailKey, (old) =>
        old ? { ...old, soaps: soapsWithField(old.soaps, key, nextItems) } : old
      )
      return { previous }
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous)
      }
      showToast('保存に失敗しました', 'error')
    },

    // 保存後はサーバ値に収束させる。legacy の楽観 toggle と違い、在宅は猶予 polling という
    // 別 writer が同じ cache を触るため楽観状態を放置しない。楽観更新済みなので
    // ユーザーはこの往復を待たない。
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })

  // items の解決も cache 起点にする (楽観更新の結果を再 render を待たずに読むため)。
  // cache 未設定時のみ props に落とす。
  const itemsOf = useCallback(
    (key: string): SoapContentItem[] => {
      const cached = queryClient.getQueryData<YakurekiDetail>(detailKey)
      return itemsOfField(cached?.soaps ?? event.soaps, key)
    },
    [queryClient, detailKey, event.soaps]
  )

  // key の items を差し替えて保存する。成功で true (失敗時の通知は onError が担う)。
  const commit = useCallback(
    async (key: string, nextItems: SoapContentItem[]) => {
      try {
        await mutateAsync({ key, nextItems })
        return true
      } catch {
        return false
      }
    },
    [mutateAsync]
  )

  const line = useLineEditing<string>({ itemsOf, commit })

  // checkbox (採否 = is_selected) トグル。
  const toggleSelect = useCallback(
    (key: string, index: number) => {
      const items = [...itemsOf(key)]
      const item = items[index]
      if (!item) return
      items[index] = { ...item, is_selected: !(item.is_selected ?? true) }
      void commit(key, items)
    },
    [itemsOf, commit]
  )

  return {
    ...line,
    toggleSelect,
  }
}
